import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { Button, Form, Icon, Divider } from "semantic-ui-react";
import { ControlledInput } from "../ControlledForm";
import queryString from "query-string";
import { OAuthIDP } from "../../hooks/useOAuth";
import useProducts from "../admin/employees/hooks/useProducts";
import { useFeatureFlag } from "../../hooks/use-feature-flag";
import { AuthMode, SSOButton } from "./SSOButton";
import classNames from "classnames";

interface ILoginFormProps {
  site: string;
  message?: React.JSX.Element;
  onSubmit: (data: any) => void;
  username: {
    label: string;
    [key: string]: any;
  };
  initiateOAuth: (idp: OAuthIDP, masquerade?: string) => void;
  isLoading?: boolean;
  adminMode?: boolean;
}

export const LoginForm = ({
  site,
  message,
  onSubmit,
  username,
  isLoading,
  initiateOAuth,
  adminMode,
}: ILoginFormProps) => {
  const { control, handleSubmit } = useFormContext();
  const { redirect } = queryString.parse(window.location.search, { parseBooleans: true });
  const [showPassword, setShowPassword] = useState(false);
  const isPublicLogin = window.location.pathname.startsWith(`/${site}/public`);
  const forgotPath = isPublicLogin ? "public/forgot" : "forgot";

  const { hasSSOMicrosoft } = useProducts();
  const { isEnabled: hasSSOGoogle } = useFeatureFlag("sso.google");
  const { isEnabled: hasSSOFacebook } = useFeatureFlag("sso.facebook");
  const { isEnabled: hasSSOApple } = useFeatureFlag("sso.apple");
  const hasAnySSO = hasSSOMicrosoft || (isPublicLogin && (hasSSOGoogle || hasSSOFacebook || hasSSOApple));

  const [masquerade, setMasquerade] = useState("");

  const renderIcon = (showPassword: boolean) => (
    <Icon
      name={showPassword ? "eye slash" : "eye"}
      link
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? "Hide password" : "Show password"}
      title={showPassword ? "Hide password" : "Show password"}
    />
  );

  function buildForgotPasswordPath() {
    if (redirect) return `/${site}/${forgotPath}?redirect=${encodeURIComponent(redirect as string)}`;
    return `/${site}/${forgotPath}`;
  }

  if (adminMode) {
    return (
      <Form>
        {message}
        <Form.Field>
          <label>User</label>
          <input value={masquerade} onChange={(e) => setMasquerade(e.target.value)} />
        </Form.Field>
        <Divider className='tw-w-0' />
        <SSOButton
          mode={AuthMode.Login}
          idp={OAuthIDP.Microsoft}
          initiateOAuth={initiateOAuth}
          isLoading={isLoading}
          masquerade={masquerade || "Test+MasterAdmin@gogovapps.com"}
        />
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {message}
      <Form.Field required>
        <label htmlFor='email'>{username.label}</label>
        <ControlledInput name='email' control={control} autoComplete='email' />
      </Form.Field>
      <Form.Field required className='tw-mb-2'>
        <label htmlFor='password'>Password</label>
        <ControlledInput
          type={showPassword ? "text" : "password"}
          name='password'
          control={control}
          icon={renderIcon(showPassword)}
          iconPosition='right'
          autoComplete='current-password'
        />
      </Form.Field>
      <div className='tw-mb-4 tw-text-right'>
        <Link to={buildForgotPasswordPath()}>Forgot Password</Link>
      </div>
      <div className='tw-mb-4 tw-flex tw-justify-center'>
        <Button
          className='tw-w-[35%] tw-min-w-fit tw-px-6 tw-py-3 tw-text-lg tw-leading-[1.125]'
          type='submit'
          primary
          size='medium'
          loading={isLoading}
          disabled={isLoading}
        >
          Sign In
        </Button>
      </div>
      {hasAnySSO && (
        <Divider horizontal className='tw-pt-2'>
          Or
        </Divider>
      )}
      {hasSSOMicrosoft && (
        <SSOButton mode={AuthMode.Login} idp={OAuthIDP.Microsoft} initiateOAuth={initiateOAuth} isLoading={isLoading} />
      )}
      {isPublicLogin && hasSSOGoogle && (
        <SSOButton mode={AuthMode.Login} idp={OAuthIDP.Google} initiateOAuth={initiateOAuth} isLoading={isLoading} />
      )}
      {isPublicLogin && hasSSOFacebook && (
        <SSOButton mode={AuthMode.Login} idp={OAuthIDP.Facebook} initiateOAuth={initiateOAuth} isLoading={isLoading} />
      )}
      {isPublicLogin && hasSSOApple && (
        <SSOButton mode={AuthMode.Login} idp={OAuthIDP.Apple} initiateOAuth={initiateOAuth} isLoading={isLoading} />
      )}
      {isPublicLogin && (
        <div
          className={classNames({
            "tw-text-md tw-text-center": true,
            "tw-p-6": hasAnySSO,
            "tw-pb-6": !hasAnySSO,
          })}
        >
          {`New user? `}
          <Link
            className='tw-text-bold'
            to={`/${site}/public/register${redirect ? "?redirect=" + encodeURIComponent(redirect as string) : ""}`}
          >
            Create an account
          </Link>
        </div>
      )}
    </Form>
  );
};

export default LoginForm;
